/**
 * AppShell — top-level layout met sidebar (Aurora), topbar (lokaal,
 * lichtgewicht) en de view-outlet voor de router. Logica is mode-aware:
 *
 *  - `superadmin` shell heeft links naar Tenants / Talen / Superadmins
 *  - `tenant` shell heeft links naar Users / Org-units / Teksten + de
 *    Dashboard-link
 *
 * Branding wordt toegepast via een useEffect die naar tenant-meta
 * watched — die hook komt in M9 (useTenantMeta). Voor nu accepteert
 * AppShell een optionele `branding` prop voor handmatige aansturing
 * tijdens scaffold-development.
 *
 * Shadow-indicator zit IN de topbar (ADR-0020): de topbar zelf krijgt
 * een modifier-class die 'm rood (superadmin-shadow-tenant-admin) of
 * amber (user-shadow) maakt.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';

import { confirmNavigation } from '@shared/navigationGuard';
import {
  House,
  GraduationCap,
  ClipboardText,
  PencilLine,
  ChatCircleText,
  Medal,
  ListChecks,
  Buildings,
  Images,
  Translate,
  UserGear,
  SignOut,
  EnvelopeSimple,
  Article,
  Gear,
  FlowArrow,
  ClockCounterClockwise,
  CheckCircle,
  ChartBar,
  Moon,
  Sun,
  List as ListIcon,
} from '@phosphor-icons/react';

import { Avatar, Sidebar, type NavGroup, type NavItem } from '@aurora';
import { ShadowIndicator } from '@shared/components/ShadowIndicator';
import { useAppMode } from '@shared/app-mode-context';
import { useAuthStore } from '@shared/stores/auth';
import { mayGradeTests } from '@shared/mayGradeTests';
import { useImpersonationStore } from '@shared/stores/impersonation';
import { useThemeStore } from '@shared/stores/theme';
import { useTenantMeta } from '@shared/hooks/useTenantMeta';
import { useCatalogMenu } from '@shared/hooks/useCatalogs';
import { ADMIN_CONSOLE_ATOM, ADMIN_NAV_GROUPS, adminNavLinks, type AdminNavGroupId } from './adminNav';
import { SettingsBreadcrumb } from './SettingsBreadcrumb';
import { SETTINGS_ATOMS, settingsPageFor, visibleSettingsPages } from './settingsPages';
import { openSelfHandoff } from '@shared/cross-host';
import { SPA_LOCALES, type SpaLocale } from '@shared/i18n/bundles';

import styles from './AppShell.module.css';

/**
 * Vanaf hoeveel catalogi ze samenklappen tot één groep (issue 135). Vier losse items passen
 * nog; tien duwen de rest van het menu weg.
 */
const CATALOG_NAV_OVERFLOW = 4;

/** Het id van die groep. Engels en taal-onafhankelijk, net als een route-segment. */
const CATALOG_NAV_GROUP = 'catalogs' as const;

interface NavLinkSpec {
  path: string;
  label: string;
  icon: ReactNode;
  /**
   * Het permissie-atoom dat dit item ontsluit (UI8). Ontbreekt het, dan is het item niet
   * rechten-gepoort — dat geldt voor de items die elke ingelogde gebruiker hoort te zien
   * (dashboard, catalogus op de app-host).
   */
  permission?: string | readonly string[];
  /**
   * De inklapbare nav-groep (issue 357). Zonder groep staat het item los.
   *
   * Naast de beheer-groepen is er één groep die niet uit `adminNav` komt: de catalogi van de
   * omgeving klappen bij een stuk of wat samen tot één groep (issue 135). Die is data van de
   * omgeving en geen vaste indeling, dus hij staat hier en niet daar.
   */
  group?: AdminNavGroupId | typeof CATALOG_NAV_GROUP;
  /** Een teller achter het label, zoals het aantal wachtende goedkeuringen. */
  badge?: number;
}

export function AppShell() {
  const { t, i18n } = useTranslation();
  const mode = useAppMode();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const impersonationMode = useImpersonationStore((s) => s.meta.mode);
  const tenantMetaQuery = useTenantMeta(mode === 'tenant');

  // Draait deze SPA op de beheer-host? (ADR-0028) Stuurt de nav-split.
  const isManageSurface = mode === 'tenant' && tenantMetaQuery.data?.surface === 'manage';
  // Eindgebruiker-host + de ingelogde user is admin → toon de "Beheer openen"-
  // link naar de manage-host (waar de admin apart inlogt, ADR-0028).
  const isAppSurface = mode === 'tenant' && tenantMetaQuery.data?.surface === 'app';
  // De catalogi in het menu van deze gebruiker (issue 135). Alleen op de app-host: op de
  // beheer-host levert het admin-blok de activiteiten-lijst, en dan zou dit dubbelen.
  const catalogMenu = useCatalogMenu(isAppSurface);
  // Heeft deze gebruiker een beheer-kant? Uit `surfaces` en niet uit het console-atoom:
  // `permissions` zijn de rechten van de host waarop `/me` werd opgevraagd (ADR-0037), en
  // op de app-host telt het console-atoom per definitie niet mee. Zou deze regel nog naar
  // het atoom kijken, dan verdween "Beheer openen" voor elke beheerder op elke omgeving.
  const mayEnterConsole = (user?.surfaces ?? []).includes('manage');

  // Thema-schakelaar (issue #95): superadmin-shell altijd; tenant-shell alleen
  // wanneer het tenant-beleid 'switch' is (App.tsx zet de mode uit de meta).
  const theme = useThemeStore((s) => s.theme);
  const themeMode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggle);
  const showThemeSwitch = mode === 'superadmin' || themeMode === 'switch';

  // Locale-keuzes die we tonen in de switcher. Op tenant-shell honoreren
  // we `tenantMeta.available_locales` (filter op SPA-bundles zodat een
  // misconfigured tenant geen "EN" toont waar geen vertaling voor is).
  // Op superadmin-shell — en als fallback vóór meta laadt — exposeren we
  // alle gebundelde SPA-locales. Die lijst komt uit de geregistreerde bundels
  // (`@shared/i18n/bundles`); een eigen kopie hier liet een nieuwe taal stil weg.
  const visibleLocales = useMemo<readonly string[]>(() => {
    if (mode === 'tenant' && tenantMetaQuery.data?.available_locales) {
      return tenantMetaQuery.data.available_locales.filter(
        (code): code is SpaLocale => (SPA_LOCALES as readonly string[]).includes(code),
      );
    }
    return SPA_LOCALES;
  }, [mode, tenantMetaQuery.data]);

  const links = useMemo<NavLinkSpec[]>(() => {
    if (mode === 'superadmin') {
      return [
        { path: '/', label: t('nav.dashboard'), icon: <House size={18} weight="regular" /> },
        { path: '/tenants', label: t('nav.tenants'), icon: <Buildings size={18} weight="regular" /> },
        { path: '/platform-assets', label: t('nav.platformAssets'), icon: <Images size={18} weight="regular" /> },
        { path: '/platform-locales', label: t('nav.platformLocales'), icon: <Translate size={18} weight="regular" /> },
        { path: '/superadmins', label: t('nav.superadmins'), icon: <UserGear size={18} weight="regular" /> },
        { path: '/platform-workflows', label: t('nav.platformWorkflows'), icon: <FlowArrow size={18} weight="regular" /> },
        { path: '/mail-templates', label: t('nav.mailTemplates'), icon: <Article size={18} weight="regular" /> },
        { path: '/mail-outbox', label: t('nav.mailOutbox'), icon: <EnvelopeSimple size={18} weight="regular" /> },
        { path: '/settings', label: t('nav.settings'), icon: <Gear size={18} weight="regular" /> },
      ];
    }
    const tenantLinks: NavLinkSpec[] = [
      { path: '/', label: t('nav.dashboard'), icon: <House size={18} weight="regular" /> },
    ];
    // De tellers achter de items waar werk op je wacht (feedback 25). Elk is wat de pagina erachter
    // toont, binnen het bereik van je recht; de Sidebar toont alleen een getal boven nul.
    const work = user?.work ?? { grading: 0, attendance: 0, questionnaires: 0 };
    // Catalogus op de eindgebruiker-host (surface 'app') — de lerende bladerkant
    // voor elke ingelogde gebruiker (D11, #179). Op de manage-host levert het
    // admin-blok hieronder de (beheer-)catalogus-link, dus geen dubbeling.
    if (isAppSurface) {
      // De catalogi van déze gebruiker (issue 135), elk een eigen menu-item. Welke erbij staan
      // bepaalt de server: gepubliceerd, mag je openen, en niet leeg voor jou — tenzij de
      // beheerder anders koos. Is er nog niets geladen, dan staat er het ene kale `/catalog`,
      // dat zelf doorstuurt naar de eerste beschikbare.
      const catalogLinks = (catalogMenu.data ?? []).filter((entry) => entry.in_main_nav);

      // ⚠️ VEEL CATALOGI KLAPPEN SAMEN TOT ÉÉN GROEP (issue 135). Een omgeving met tien
      // catalogi zou anders tien items boven *Mijn inschrijvingen* duwen, en dan is het menu
      // geen menu meer. De grens ligt bij vier: daaronder is een groep met één klik erbij
      // duurder dan de ruimte die hij wint, daarboven precies andersom. De groep staat
      // standaard open, want hij is het hoofdgebruik van dit scherm.
      const grouped = catalogLinks.length > CATALOG_NAV_OVERFLOW;

      tenantLinks.push(
        ...(catalogLinks.length > 0
          ? catalogLinks.map((entry) => ({
            path: `/catalog/${entry.slug}`,
            label: entry.name,
            icon: <GraduationCap size={18} weight="regular" />,
            group: grouped ? CATALOG_NAV_GROUP : undefined,
          }))
          : [{ path: '/catalog', label: t('nav.catalog'), icon: <GraduationCap size={18} weight="regular" /> }]),
        { path: '/my-enrollments', label: t('nav.myEnrollments'), icon: <ListChecks size={18} weight="regular" /> },
        { path: '/my-qualifications', label: t('nav.myQualifications'), icon: <Medal size={18} weight="regular" /> },
        {
          path: '/my-questionnaires',
          label: t('nav.myQuestionnaires'),
          icon: <ChatCircleText size={18} weight="regular" />,
          badge: work.questionnaires,
        },
      );
    }
    // Presentie: voor wie het atoom houdt, ongeacht host. Een docent werkt op de
    // eindgebruiker-host (hij heeft geen console-recht), maar een beheerder die het
    // atoom omgevings-breed houdt wil er op de beheer-host ook bij — dus geen
    // surface-conditie, alleen het recht.
    if ((user?.permissions ?? []).includes('attendance.manage')) {
      tenantLinks.push({
        path: '/attendance',
        label: t('nav.attendance'),
        icon: <ClipboardText size={18} weight="regular" />,
        badge: work.attendance,
      });
    }
    // Nakijken (basis-T): hetzelfde plan als Presentie — voor wie het atoom houdt, ongeacht host.
    // Een docent die toetsen nakijkt heeft geen beheer-recht en dus ook geen beheermenu Toetsen.
    // Ook voor wie als nakijker is aangewezen zonder het atoom (besluit PO 16-10): `mayGradeTests`.
    if (mayGradeTests(user)) {
      tenantLinks.push({
        path: '/grading',
        label: t('nav.grading'),
        icon: <PencilLine size={18} weight="regular" />,
        badge: work.grading,
      });
    }
    // Resultaten (vragenlijsten-slice 1b): de uitkomst van evaluaties lezen, hetzelfde plan als Presentie en
    // Nakijken — voor wie het atoom houdt, ongeacht host. Een docent leest zijn eigen uitvoeringen en heeft
    // geen beheer-recht en dus ook het beheermenu Vragenlijsten niet.
    if ((user?.permissions ?? []).includes('questionnaire.results')) {
      tenantLinks.push({
        path: '/results',
        label: t('nav.results'),
        icon: <ChartBar size={18} weight="regular" />,
      });
    }
    // Goedkeuringen: alleen als er iets op je wacht, of als je het HR-vangnet bent
    // (ADR-0037, besluit 5). Goedkeuren hangt niet aan een recht maar aan de taak: wie hem
    // krijgt, beslist, en dat volgt uit de organisatie en niet uit een rol. Een item voor
    // iedereen was voor de meesten een lege lijst, en wie wél iets had zag niet dat het
    // er lag. Het vangnet houdt het item ook zonder taak, want daar landt een goedkeuring
    // die geen goedkeurder vond. De inbox-route zelf blijft voor iedereen open: een link
    // uit een mail moet werken, ook als het getal hier nog niet ververst is.
    const pendingApprovals = user?.approvals?.pending ?? 0;
    if (pendingApprovals > 0 || (user?.permissions ?? []).includes('approval.handle')) {
      tenantLinks.push({
        path: '/approvals',
        label: t('nav.approvals'),
        icon: <CheckCircle size={18} weight="regular" />,
        badge: pendingApprovals,
      });
    }
    // Het beheer-overzicht (issue 912) staat náást de inbox en niet erin: de inbox is
    // wat op jóu wacht, dit is wat er in de omgeving vastzit. Alleen op de beheer-host,
    // en alleen met `approval.handle` — hetzelfde recht dat het HR-vangnet vormt.
    // Mét badge, en dat draait de eerdere keuze om (besluit Nick 19-10): een teller telt alles wat je
    // mag zien, dus hier alle open goedkeuringen in de omgeving — precies wat het overzicht toont.
    if (isManageSurface && (user?.permissions ?? []).includes('approval.handle')) {
      tenantLinks.push({
        path: '/open-approvals',
        label: t('nav.approvalsOverview'),
        icon: <ListChecks size={18} weight="regular" />,
        badge: user?.approvals?.open ?? 0,
      });
    }
    // Beheer-nav verschijnt alleen op de manage-host (ADR-0028): de
    // eindgebruiker-host (surface 'app') toont nooit admin-tooling, ook niet
    // voor een tenant-admin. Welke items erin staan is een rechten-vraag en leeft
    // daarom in `adminNav` — met een eigen test (UI8).
    const held = user?.permissions ?? [];

    if (isManageSurface) {
      tenantLinks.push(...adminNavLinks(t, held));
    }
    // Logboek: eigen recht, niet "is admin" — inzage is te delegeren aan een auditor
    // (R-005). Stond in de menugroep Instellingen; die is weg (feedback 21, experiment), en het
    // logboek is geen instelling maar inzage, dus het staat los onderaan, boven Instellingen.
    if (isManageSurface && held.includes('audit.view')) {
      tenantLinks.push(
        { path: '/audit-log', label: t('nav.auditLog'), icon: <ClockCounterClockwise size={18} weight="regular" /> },
      );
    }
    // Instellingen staat er zodra er minstens één tegel op de startpagina voor je is — niet alleen met
    // `tenant_config.view`. Wie alleen workflows mag beheren, komt er sinds de menugroep weg is via hier.
    if (isManageSurface && held.includes(ADMIN_CONSOLE_ATOM) && visibleSettingsPages(held, user?.hidden_menu_items ?? []).length > 0) {
      // Generieke Instellingen-hub (FB-6): bewust als láátste, visueel losgekoppeld
      // van de feature-items — een config-hub hoort onderaan de nav, niet tussen de
      // beheer-schermen. Vervangt de oude mid-nav "Catalogus-instellingen"-link.
      // Blijft bewust **buiten** de groepen: een hub hoort onderaan, en de Sidebar
      // houdt de volgorde van deze lijst aan.
      tenantLinks.push(
        { path: '/settings', label: t('nav.settings'), icon: <Gear size={18} weight="regular" />, permission: SETTINGS_ATOMS },
      );
    }
    // Module-laag (issue 1052): wat een uitgezette module als menu-item declareert bestaat voor
    // deze omgeving niet. De server stuurt alleen paden, de shell hoeft geen module te kennen.
    const hidden = user?.hidden_menu_items ?? [];
    return hidden.length === 0 ? tenantLinks : tenantLinks.filter((link) => !hidden.includes(link.path));
  }, [mode, user, t, isManageSurface, isAppSurface, catalogMenu.data]);

  // NavItem-array voor de Sidebar — id is hier het URL-path zodat we makkelijk
  // kunnen mappen tussen sidebar-click en router-navigate. `href` is datzelfde pad:
  // het item is daarmee een echte link, zodat ctrl/midden-klik een tabblad opent
  // (issue 41) terwijl de gewone klik door de navigatie-guard blijft gaan.
  const items: NavItem[] = useMemo(
    () =>
      links.map((l) => ({
        id: l.path,
        label: l.label,
        icon: l.icon,
        href: l.path,
        group: l.group,
        badge: l.badge,
      })),
    [links],
  );

  // De groepen zijn data (issue 357): welke er zijn en wat erin zit staat in
  // `adminNav`, niet in het component. De indeling is een vertrekpunt — PO/PM
  // mogen 'm aanscherpen, en dat is dan één regel daar.
  const navGroups: NavGroup[] = useMemo(() => {
    const groups: NavGroup[] = ADMIN_NAV_GROUPS.map((g) => ({
      id: g.id,
      label: t(g.labelKey),
      defaultOpen: g.defaultOpen,
    }));

    // De catalogus-groep alleen als er iets in zit: een lege groep is een kopje zonder inhoud,
    // en bij vier of minder catalogi staan ze juist bewust los.
    if (links.some((l) => l.group === CATALOG_NAV_GROUP)) {
      groups.unshift({ id: CATALOG_NAV_GROUP, label: t('nav.groupCatalogs'), defaultOpen: true });
    }

    return groups;
  }, [t, links]);

  // Active-item op basis van current pathname. Voor de root-route ("/")
  // matchen we exact; voor sub-paths ("/users/123") matchen we het
  // langste prefix-pad uit onze nav-set.
  //
  // Een pagina die bij geen menu-item hoort (de lijst achter een dashboardtaak, een bewerkpagina)
  // markeert niets. Hier stond eerst `?? '/'`, en dan stond *Dashboard* aan op elke pagina die
  // niet het dashboard is.
  // Een pagina die onder Instellingen valt (een tegel op de startpagina, ook met een eigen adres als
  // `/workflows`) laat *Instellingen* oplichten: zo zie je in het menu waar je bent, ook al staat de
  // pagina er zelf niet meer in.
  const settingsPage = isManageSurface ? settingsPageFor(location.pathname) : null;
  const activeItem = useMemo(() => {
    if (settingsPage && items.some((i) => i.id === '/settings')) return '/settings';
    const sorted = items
      .map((i) => i.id)
      .sort((a, b) => b.length - a.length);
    return sorted.find((path) =>
      path === '/' ? location.pathname === '/' : location.pathname.startsWith(path),
    );
  }, [items, location.pathname, settingsPage]);

  const topbarClass = [
    styles.topbar,
    impersonationMode ? styles[`topbar--shadow-${impersonationMode}`] : '',
  ]
    .filter(Boolean)
    .join(' ');

  // App-naam: op tenant-shell met branding.name_in_ui-override (anders
  // de tenant.name uit /api/v1/tenant) — komt overeen met de Vue-App.vue
  // title-computed (zie git-archive cdb1537).
  const appName = useMemo(() => {
    if (mode === 'superadmin') return t('app.superadmin.title');
    const meta = tenantMetaQuery.data;
    return meta?.branding?.name_in_ui ?? meta?.name ?? t('app.tenant.title');
  }, [mode, tenantMetaQuery.data, t]);

  /**
   * Mobile-drawer state. Bij narrow viewports (<768px) is de sidebar
   * een off-canvas drawer; deze state toggelt 'm. Wide viewports
   * ignoreren de waarde (CSS doet dat via media-query).
   */
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Sluit drawer op Escape — standaard dialog-pattern.
  useEffect(() => {
    if (!mobileNavOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMobileNavOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  // Sluit drawer bij route-wissel (anders blijft 'ie open na nav-click).
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  function changeLocale(code: string) {
    if (code === i18n.language) return;
    void i18n.changeLanguage(code);
    // Persisted choice voor next-load: localStorage; i18next-browser-
    // languagedetector zou dit later kunnen overnemen, voor nu manueel.
    try {
      window.localStorage.setItem('locale', code);
    } catch {
      // private browsing / quotaExceeded — niet kritiek, locale werkt
      // gewoon voor de huidige sessie.
    }
  }

  const sidebarWrapClass = [
    styles.sidebarWrap,
    mobileNavOpen ? styles.sidebarOpen : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.shell}>
      {/*
        Sidebar als peer-van-content-column. Wide: loopt van TOP tot
        BOTTOM, Aurora-logo bovenaan, topbar alleen boven main. Narrow
        (<768px): sidebar wordt off-canvas drawer, getoggled via een
        hamburger-knop in de topbar; backdrop sluit hem ook.
      */}
      <div className={sidebarWrapClass} role={mobileNavOpen ? 'dialog' : undefined} aria-modal={mobileNavOpen ? 'true' : undefined}>
        <Sidebar
          items={items}
          groups={navGroups}
          // Per omgeving-oppervlak een eigen sleutel: de beheer-nav en de
          // eindgebruiker-nav zijn verschillende menu's en delen geen stand.
          groupStateKey={`nav-groups:${mode}`}
          activeItem={activeItem}
          onItemClick={(id) => {
            // Views met unsaved changes (bv. tenant-instellingen) kunnen
            // een guard registreren — die mag de navigatie tegenhouden.
            if (!confirmNavigation()) return;
            navigate(id);
            setMobileNavOpen(false);
          }}
          appName={appName}
          ariaLabel={t('nav.mainNavigation')}
          badgeLabel={(count) => t('nav.badgePending', { count })}
        />
      </div>
      {mobileNavOpen && (
        <button
          type="button"
          className={styles.backdrop}
          onClick={() => setMobileNavOpen(false)}
          aria-label={t('app.closeMenu')}
        />
      )}
      <div className={styles.content}>
        <header className={topbarClass}>
          <button
            type="button"
            className={styles.hamburger}
            onClick={() => setMobileNavOpen(true)}
            aria-label={t('app.openMenu')}
            aria-expanded={mobileNavOpen}
          >
            <ListIcon size={20} weight="regular" />
          </button>
          <ShadowIndicator />
          <div className={styles.topbarSpacer} />
          {isAppSurface && mayEnterConsole && tenantMetaQuery.data?.manage_host && (
            <button
              type="button"
              className={styles.manageLink}
              onClick={() => void openSelfHandoff(tenantMetaQuery.data!.manage_host)}
            >
              <Gear size={16} weight="regular" aria-hidden="true" />
              {t('nav.openAdminConsole')}
            </button>
          )}
          {isManageSurface && tenantMetaQuery.data?.app_host && (
            <button
              type="button"
              className={styles.manageLink}
              onClick={() => void openSelfHandoff(tenantMetaQuery.data!.app_host)}
            >
              <House size={16} weight="regular" aria-hidden="true" />
              {t('nav.openApp')}
            </button>
          )}
          {showThemeSwitch && (
            <button
              type="button"
              className={styles.themeSwitch}
              onClick={toggleTheme}
              aria-pressed={theme === 'dark'}
              aria-label={theme === 'dark' ? t('nav.themeLight') : t('nav.themeDark')}
              title={theme === 'dark' ? t('nav.themeLight') : t('nav.themeDark')}
            >
              {theme === 'dark'
                ? <Sun size={16} weight="regular" aria-hidden="true" />
                : <Moon size={16} weight="regular" aria-hidden="true" />}
            </button>
          )}
          {visibleLocales.length > 1 && (
            <div className={styles.localeSwitcher}>
              <Translate size={16} weight="regular" aria-hidden="true" />
              <LocaleDropdown
                current={i18n.language}
                options={visibleLocales}
                onChange={changeLocale}
                ariaLabel={t('nav.changeLanguage')}
              />
            </div>
          )}
          {user && (
            <div className={styles.userInfo}>
              <Avatar size="sm" src={user.profile_picture_url} name={user.display_name} />
              <span className={styles.userName}>{user.display_name}</span>
              <button
                type="button"
                className={styles.logoutBtn}
                onClick={handleLogout}
                aria-label={t('nav.logout')}
                title={t('nav.logout')}
              >
                <SignOut size={18} weight="regular" />
              </button>
            </div>
          )}
        </header>
        <main className={mode === 'tenant' ? `${styles.main} ${styles.mainStart}` : styles.main}>
          {settingsPage && <SettingsBreadcrumb page={settingsPage} />}
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * Compact locale-dropdown — geen native <select> (browser stylet de
 * option-list zelf, niet matchend met Aurora's topbar-rim) maar een
 * eigen popover-listbox die de paarse rim wel doortrekt naar het
 * geopende menu. Click-outside sluit de menu; Escape ook.
 */
function LocaleDropdown({
  current,
  options,
  onChange,
  ariaLabel,
}: {
  current: string;
  options: readonly string[];
  onChange: (code: string) => void;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div className={styles.localeDropdown} ref={ref}>
      <button
        type="button"
        className={styles.localeDropdownTrigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
      >
        {current.toUpperCase()}
        <span className={styles.localeDropdownCaret} aria-hidden="true">▾</span>
      </button>
      {open && (
        <ul className={styles.localeDropdownMenu} role="listbox">
          {options.map((code) => (
            <li key={code}>
              <button
                type="button"
                role="option"
                aria-selected={code === current}
                className={`${styles.localeDropdownOption} ${code === current ? styles.localeDropdownOptionActive : ''}`}
                onClick={() => {
                  onChange(code);
                  setOpen(false);
                }}
              >
                {code.toUpperCase()}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
